import { useState } from 'react';
import PanelLayout from '../components/PanelLayout';
import { therapistProfile } from '@/mocks/therapistPanel';

const expertiseOptions = [
  'Masaj Terapisi',
  'Aromaterapi',
  'Hamam',
  'Cilt Bakımı',
  'Refleksoloji',
  'Spa Terapisi',
  'Kese & Köpük',
];

const experienceOptions = ['0-1 yıl', '1-3 yıl', '3-5 yıl', '5+ yıl'];
const salaryOptions = ['45K-60K TL', '60K-70K TL', '70K-90K TL', '90K-120K TL'];
const ssgOptions = ['Zorunlu', 'Müzakereye Açığım', 'Fark Etmez'];
const accommodationOptions = ['İstiyorum', 'İstemiyorum'];
const workModelOptions = ['Tam zamanlı', 'Part-time', 'Sezonluk'];
const keseOptions = ['Evet', 'Hayır', 'Müzakereye Açığım'];
const cityOptions = ['İstanbul', 'Ankara', 'İzmir', 'Antalya', 'Bodrum', 'Kayseri'];
const pastExperienceOptions = ['Otel Spa', 'Wellness Merkezi', 'Termal Tesis', 'Güzellik Salonu', 'Hastane Spa'];

export default function ProfilimPage() {
  const [form, setForm] = useState(therapistProfile);
  const [saved, setSaved] = useState(false);

  const toggleArray = (field: keyof typeof form, value: string) => {
    const current = (form[field] as string[]) || [];
    if (current.includes(value)) {
      setForm({ ...form, [field]: current.filter((v) => v !== value) });
    } else {
      setForm({ ...form, [field]: [...current, value] });
    }
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const calcCompletion = () => {
    let score = 0;
    if (form.name) score += 10;
    if (form.expertise.length) score += 15;
    if (form.experience) score += 10;
    if (form.cityPreferences.length) score += 10;
    if (form.salaryBand) score += 10;
    if (form.workModel) score += 5;
    if (form.bio.length > 50) score += 15;
    if (form.hasIdDoc && form.idDocStatus === 'Onaylandı') score += 10;
    if (form.hasCertificate) score += 10;
    if (form.hasPhoto) score += 5;
    return Math.min(score, 100);
  };

  const completion = calcCompletion();

  return (
    <PanelLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-deep-navy-500">Profilim</h1>
          <p className="text-sm text-gray-500 mt-1">Profil bilgilerinizi güncelleyin ve işletmelere kendinizi en iyi şekilde tanıtın.</p>
        </div>

        {/* Completion Bar */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-deep-navy-500">Profil Tamamlama</span>
            <span className="text-sm font-bold text-dark-teal-500">{completion}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-dark-teal-500 rounded-full transition-all" style={{ width: `${completion}%` }} />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Daha yüksek tamamlama oranı, daha iyi eşleşme fırsatları demektir.
          </p>
        </div>

        {/* Documents Status */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
          <h2 className="text-sm font-semibold text-deep-navy-500 mb-3">Belge Durumları</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500">
                <i className="ri-image-line text-base" />
              </span>
              <div>
                <p className="text-xs font-medium text-deep-navy-500">Fotoğraf</p>
                <span className={`text-[10px] font-medium ${form.hasPhoto ? 'text-green-600' : 'text-gray-400'}`}>
                  {form.hasPhoto ? 'Yüklendi' : 'Yüklenmedi'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500">
                <i className="ri-file-user-line text-base" />
              </span>
              <div>
                <p className="text-xs font-medium text-deep-navy-500">Kimlik Belgesi</p>
                <span className="text-[10px] font-medium text-green-600">{form.idDocStatus}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500">
                <i className="ri-award-line text-base" />
              </span>
              <div>
                <p className="text-xs font-medium text-deep-navy-500">Sertifika/Diploma</p>
                <span className="text-[10px] font-medium text-amber-600">{form.certificateStatus}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-6">
          {/* Expertise */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">Uzmanlıklar</label>
            <div className="flex flex-wrap gap-2">
              {expertiseOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => toggleArray('expertise', opt)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    form.expertise.includes(opt)
                      ? 'bg-dark-teal-500 text-white border-dark-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">Deneyim Süresi</label>
            <div className="flex flex-wrap gap-2">
              {experienceOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setForm({ ...form, experience: opt })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    form.experience === opt
                      ? 'bg-dark-teal-500 text-white border-dark-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Past Experience */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">Geçmiş Deneyim Alanları</label>
            <p className="text-xs text-gray-400 mb-2">Zorunlu değil</p>
            <div className="flex flex-wrap gap-2">
              {pastExperienceOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => toggleArray('pastExperience', opt)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    (form.pastExperience as string[]).includes(opt)
                      ? 'bg-dark-teal-500 text-white border-dark-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Preferred Work Areas */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">Tercih Ettiği Çalışma Alanları</label>
            <p className="text-xs text-gray-400 mb-2">Zorunlu değil</p>
            <div className="flex flex-wrap gap-2">
              {pastExperienceOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => toggleArray('preferredWorkAreas', opt)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    (form.preferredWorkAreas as string[]).includes(opt)
                      ? 'bg-dark-teal-500 text-white border-dark-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* City Preferences */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">Şehir Tercihleri</label>
            <div className="flex flex-wrap gap-2">
              {cityOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => toggleArray('cityPreferences', opt)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    form.cityPreferences.includes(opt)
                      ? 'bg-dark-teal-500 text-white border-dark-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Salary */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">Maaş Beklentisi</label>
            <div className="flex flex-wrap gap-2">
              {salaryOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setForm({ ...form, salaryBand: opt })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    form.salaryBand === opt
                      ? 'bg-dark-teal-500 text-white border-dark-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* SSG */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">SGK Tercihi</label>
            <div className="flex flex-wrap gap-2">
              {ssgOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setForm({ ...form, ssgPreference: opt })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    form.ssgPreference === opt
                      ? 'bg-dark-teal-500 text-white border-dark-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Accommodation */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">Konaklama Tercihi</label>
            <div className="flex flex-wrap gap-2">
              {accommodationOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setForm({ ...form, accommodation: opt })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    form.accommodation === opt
                      ? 'bg-dark-teal-500 text-white border-dark-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Work Model */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">Çalışma Modeli</label>
            <div className="flex flex-wrap gap-2">
              {workModelOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setForm({ ...form, workModel: opt })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    form.workModel === opt
                      ? 'bg-dark-teal-500 text-white border-dark-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Kese */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">Kese & Köpük</label>
            <div className="flex flex-wrap gap-2">
              {keseOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setForm({ ...form, kesePreference: opt })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    form.kesePreference === opt
                      ? 'bg-dark-teal-500 text-white border-dark-teal-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Mini Bio */}
          <div>
            <label className="block text-sm font-medium text-deep-navy-500 mb-2">Mini Bio</label>
            <p className="text-xs text-gray-400 mb-2">Maksimum 300 karakter</p>
            <div className="relative">
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 300) })}
                rows={4}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-deep-navy-500 focus:outline-none focus:border-dark-teal-500 focus:ring-1 focus:ring-dark-teal-500/20 resize-none"
                placeholder="Kendinizi kısaca tanıtın..."
              />
              <span className="absolute bottom-2 right-3 text-[10px] text-gray-400">
                {form.bio.length}/300
              </span>
            </div>
            <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
              <i className="ri-error-warning-line" />
              Mini bio içinde telefon, WhatsApp veya Instagram bilgisi yazılamaz.
            </p>
          </div>

          {/* File Uploads */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-deep-navy-500 mb-2">Fotoğraf</label>
              <p className="text-xs text-gray-400 mb-2">Zorunlu değil</p>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-gray-300 transition-colors cursor-pointer">
                <span className="w-8 h-8 flex items-center justify-center mx-auto text-gray-400 mb-1">
                  <i className="ri-image-add-line text-xl" />
                </span>
                <p className="text-xs text-gray-500">Fotoğraf yükle</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-deep-navy-500 mb-2">Kimlik Belgesi</label>
              <p className="text-xs text-gray-400 mb-2">Zorunlu değil</p>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-gray-300 transition-colors cursor-pointer">
                <span className="w-8 h-8 flex items-center justify-center mx-auto text-gray-400 mb-1">
                  <i className="ri-file-upload-line text-xl" />
                </span>
                <p className="text-xs text-gray-500">PDF veya resim</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-deep-navy-500 mb-2">Sertifika/Diploma</label>
              <p className="text-xs text-gray-400 mb-2">Zorunlu değil</p>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-gray-300 transition-colors cursor-pointer">
                <span className="w-8 h-8 flex items-center justify-center mx-auto text-gray-400 mb-1">
                  <i className="ri-award-line text-xl" />
                </span>
                <p className="text-xs text-gray-500">PDF veya resim</p>
              </div>
            </div>
          </div>

          {/* Save */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={handleSave}
              className="px-6 py-2.5 rounded-lg bg-dark-teal-500 text-white text-sm font-semibold hover:bg-dark-teal-600 transition-colors cursor-pointer"
            >
              Değişiklikleri Kaydet
            </button>
            {saved && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <i className="ri-check-line" /> Kaydedildi
              </span>
            )}
          </div>
        </div>
      </div>
    </PanelLayout>
  );
}